/**
 * Nome pesquisável de uma cidade (módulo puro — sem IA, sem Maps, sem créditos).
 *
 * Problema real: um utilizador escreve "Nova Iorque" e o geocoder devolve o
 * nome TRADUZIDO ("Nova Iorque, Estados Unidos da América"). Esse nome
 * traduzido é depois usado nas pesquisas de locais e a IA/Places não encontra
 * nada — a rota vem vazia. A solução é genérica: sempre que conhecemos o nome
 * local/oficial (endónimo, `namedetails.name` do OSM), é esse que segue para
 * as pesquisas.
 *
 * O dicionário abaixo é só a rede de segurança offline para os exónimos mais
 * comuns; o caminho normal é o endónimo devolvido pelo geocoder.
 */

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase()
    .replace(/\s+/gu, " ")
    .trim();

/** Exónimos frequentes (PT/ES/FR/IT/DE) → nome local/oficial. */
const EXONYMS: Record<string, string> = {
  "nova iorque": "New York",
  "nueva york": "New York",
  "new york city": "New York",
  londres: "London",
  london: "London",
  "londra": "London",
  parigi: "Paris",
  paris: "Paris",
  roma: "Rome",
  rom: "Rome",
  milao: "Milano",
  milan: "Milano",
  mailand: "Milano",
  florenca: "Firenze",
  florence: "Firenze",
  florenz: "Firenze",
  veneza: "Venezia",
  venice: "Venezia",
  venedig: "Venezia",
  napoles: "Napoli",
  naples: "Napoli",
  turim: "Torino",
  turin: "Torino",
  genebra: "Genève",
  geneva: "Genève",
  genf: "Genève",
  zurique: "Zürich",
  zurich: "Zürich",
  munique: "München",
  munich: "München",
  colonia: "Köln",
  cologne: "Köln",
  viena: "Wien",
  vienna: "Wien",
  vienne: "Wien",
  praga: "Praha",
  prague: "Praha",
  prag: "Praha",
  varsovia: "Warszawa",
  warsaw: "Warszawa",
  moscovo: "Moscow",
  moscou: "Moscow",
  moscu: "Moscow",
  copenhaga: "København",
  copenhagen: "København",
  kopenhagen: "København",
  estocolmo: "Stockholm",
  helsinquia: "Helsinki",
  bruxelas: "Brussels",
  bruselas: "Brussels",
  bruxelles: "Brussels",
  haia: "Den Haag",
  "the hague": "Den Haag",
  atenas: "Athens",
  athenes: "Athens",
  athens: "Athens",
  αθηνα: "Athens",
  bucareste: "București",
  bucharest: "București",
  bucarest: "București",
  belgrado: "Beograd",
  belgrade: "Beograd",
  sofia: "Sofia",
  lisboa: "Lisboa",
  lisbon: "Lisboa",
  lisbonne: "Lisboa",
  lissabon: "Lisboa",
  sevilha: "Sevilla",
  seville: "Sevilla",
  saragoca: "Zaragoza",
  "corunha": "A Coruña",
  cadiz: "Cádiz",
  "cidade do mexico": "Ciudad de México",
  "mexico city": "Ciudad de México",
  "cidade do cabo": "Cape Town",
  "nova deli": "New Delhi",
  "nueva delhi": "New Delhi",
  toquio: "Tokyo",
  tokio: "Tokyo",
  quioto: "Kyoto",
  kioto: "Kyoto",
  osaca: "Osaka",
  pequim: "Beijing",
  pekin: "Beijing",
  xangai: "Shanghai",
  shangai: "Shanghai",
  "hong kong": "Hong Kong",
  seul: "Seoul",
  singapura: "Singapore",
  banguecoque: "Bangkok",
  bangcoc: "Bangkok",
  "buenos aires": "Buenos Aires",
  "rio de janeiro": "Rio de Janeiro",
  "sao paulo": "São Paulo",
  marraquexe: "Marrakesh",
  marrakech: "Marrakesh",
  cairo: "Cairo",
  "o cairo": "Cairo",
  istambul: "İstanbul",
  istanbul: "İstanbul",
  jerusalem: "Jerusalem",
  "nova orleaes": "New Orleans",
  "nova orleans": "New Orleans",
  filadelfia: "Philadelphia",
};

/** Converte um exónimo conhecido no nome local/oficial da cidade. */
export function canonicalCityName(name: string): string {
  const raw = (name ?? "").trim();
  if (!raw) return raw;
  return EXONYMS[fold(raw)] ?? raw;
}

/**
 * Identificador comparável entre nomes localizados, endónimos e nomes em
 * alfabetos diferentes (ex.: Atenas / Athens / Αθήνα).
 */
export function comparableCityName(name: string): string {
  return fold(canonicalCityName(name));
}

/**
 * Nome a usar nas pesquisas (Places/IA): o endónimo devolvido pelo geocoder
 * quando existe; caso contrário, o exónimo traduzido para nome local.
 */
export function preferSearchableCityName(
  localizedName: string | null | undefined,
  endonym?: string | null,
): string {
  const localized = (localizedName ?? "").trim();
  const local = (endonym ?? "").trim();
  if (local) return local;
  return canonicalCityName(localized);
}
